import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { Zap, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';

const DeepSeekApproachSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Emergent reasoning via pure Reinforcement Learning without human priors"
      >
        DeepSeek: Pure RL Approach
      </SlideTitle>
      
      <SlideContent>
        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <div className="slide-card mb-4 border-primary/30">
              <h3 className="font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                Core Philosophy
              </h3>
              <p className="text-muted-foreground text-sm">
                Human-defined reasoning patterns <strong className="text-destructive">limit model exploration</strong>. 
                Instead, incentivize reasoning through large-scale RL directly on the base model, 
                <strong className="text-primary"> bypassing traditional SFT</strong>.
              </p>
            </div>
            
            <div className="slide-card mb-4">
              <h3 className="font-heading font-semibold text-foreground mb-3">GRPO vs PPO</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="font-semibold text-muted-foreground">PPO (Traditional)</p>
                  <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                    <li>• Requires value model</li>
                    <li>• Memory intensive</li>
                    <li>• Complex training</li>
                  </ul>
                </div>
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                  <p className="font-semibold text-primary">GRPO (DeepSeek)</p>
                  <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                    <li>• No value model needed</li>
                    <li>• Group-relative advantages</li>
                    <li>• More efficient</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="slide-card border-accent/30">
              <h3 className="font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-accent" />
                Reward Mechanism
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                <strong className="text-accent">Outcome-based rule rewards</strong> only:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Did the model get the math answer correct?</li>
                <li>• No neural Process Reward Models (PRM)</li>
                <li>• Avoids "reward hacking"</li>
              </ul>
            </div>
          </div>
          
          <div>
            <div className="slide-card h-full">
              <h3 className="font-heading font-semibold text-foreground mb-4">Evolution: R1-Zero → R1</h3>
              
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-secondary/10 border border-secondary/30">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center text-xs font-mono text-secondary">1</div>
                    <span className="font-semibold text-foreground">DeepSeek-R1-Zero</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Pure RL on base model. Reasoning behaviors emerged naturally:</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-xs px-2 py-1 rounded bg-secondary/20 text-secondary">Self-verification</span>
                    <span className="text-xs px-2 py-1 rounded bg-secondary/20 text-secondary">Backtracking</span>
                    <span className="text-xs px-2 py-1 rounded bg-secondary/20 text-secondary">"Aha moment"</span>
                  </div>
                  <p className="text-xs text-destructive mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Language mixing, poor readability
                  </p>
                </div>
                
                <div className="flex justify-center">
                  <RefreshCw className="w-5 h-5 text-muted-foreground" />
                </div>
                
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-mono text-primary">2</div>
                    <span className="font-semibold text-foreground">DeepSeek-R1</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Added "cold start" data before RL:</p>
                  <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                    <li>• Small set of readable CoT examples</li>
                    <li>• Fixes language mixing</li>
                    <li>• Human-friendly formatting</li>
                  </ul>
                  <p className="text-xs text-accent mt-2 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Production-ready reasoning
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default DeepSeekApproachSlide;
